import { motion } from 'framer-motion';
import { useGameStore } from '../../store/gameStore';

interface GameControlsProps {
  onHint: () => void;
  hintsRemaining: number;
  onValidate?: () => void;
}

export default function GameControls({ onHint, hintsRemaining }: GameControlsProps) {
  const {
    undo,
    clearCell,
    toggleNoteMode,
    isNoteMode,
    historyIndex,
  } = useGameStore();

  const canUndo = historyIndex > 0;

  return (
    <div className="flex items-center justify-center gap-2 sm:gap-3">
      {/* Erase Button - Gray */}
      <motion.button
        className="flex flex-col items-center justify-center gap-0.5 sm:gap-1 px-3 sm:px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors touch-manipulation min-w-[52px] sm:min-w-[60px]"
        onClick={clearCell}
        whileTap={{ scale: 0.95 }}
      >
        <svg className="w-5 h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
        </svg>
        <span className="text-[10px] sm:text-xs text-gray-700 font-medium">擦除</span>
      </motion.button>

      {/* Undo Button - Gray */}
      <motion.button
        className={`flex flex-col items-center justify-center gap-0.5 sm:gap-1 px-3 sm:px-4 py-2 rounded-lg transition-colors touch-manipulation min-w-[52px] sm:min-w-[60px] ${
          canUndo 
            ? 'bg-gray-200 hover:bg-gray-300' 
            : 'bg-gray-100 opacity-50 cursor-not-allowed'
        }`}
        onClick={undo}
        disabled={!canUndo}
        whileTap={canUndo ? { scale: 0.95 } : {}}
      >
        <svg className="w-5 h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
        </svg>
        <span className="text-[10px] sm:text-xs text-gray-700 font-medium">撤回</span>
      </motion.button>

      {/* Notes Button - Green with Toggle */}
      <div className="relative">
        <motion.button
          className={`flex flex-col items-center justify-center gap-0.5 sm:gap-1 px-3 sm:px-4 py-2 rounded-lg transition-colors touch-manipulation min-w-[52px] sm:min-w-[60px] ${
            isNoteMode 
              ? 'bg-green-600 hover:bg-green-700' 
              : 'bg-green-500 hover:bg-green-600'
          }`}
          onClick={toggleNoteMode}
          whileTap={{ scale: 0.95 }}
        >
          <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
          </svg>
          <span className="text-[10px] sm:text-xs text-white font-medium">笔记</span>
        </motion.button>
        {/* Toggle Switch - positioned below */}
        <div className="absolute -bottom-4 sm:-bottom-5 left-1/2 -translate-x-1/2">
          <button
            className={`relative inline-flex h-3 sm:h-4 w-6 sm:w-8 items-center rounded-full transition-colors touch-manipulation ${
              isNoteMode ? 'bg-green-500' : 'bg-gray-300'
            }`}
            onClick={toggleNoteMode}
          >
            <span
              className={`inline-block h-2 sm:h-3 w-2 sm:w-3 transform rounded-full bg-white transition-transform ${
                isNoteMode ? 'translate-x-3 sm:translate-x-4' : 'translate-x-0.5'
              }`}
            />
          </button>
        </div>
      </div>

      {/* Hint Button - Green */}
      <motion.button
        className={`flex flex-col items-center justify-center gap-0.5 sm:gap-1 px-3 sm:px-4 py-2 rounded-lg transition-colors touch-manipulation min-w-[52px] sm:min-w-[60px] ${
          hintsRemaining > 0
            ? 'bg-green-500 hover:bg-green-600' 
            : 'bg-gray-200 opacity-50 cursor-not-allowed'
        }`}
        onClick={onHint}
        disabled={hintsRemaining <= 0}
        whileTap={hintsRemaining > 0 ? { scale: 0.95 } : {}}
      >
        <div className="relative">
          <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <span className="absolute -top-1 -right-1 text-[0.5rem] sm:text-[0.6rem] font-bold text-white bg-green-600 rounded-full w-3 h-3 sm:w-4 sm:h-4 flex items-center justify-center">
            {hintsRemaining}
          </span>
        </div>
        <span className={`text-[10px] sm:text-xs font-medium ${hintsRemaining > 0 ? 'text-white' : 'text-gray-700'}`}>
          提示
        </span>
      </motion.button>
    </div>
  );
}

